// Control-bar styles, injected once. Every color/radius/font reads from a
// --havik-* CSS variable (set by applyTheme), so the whole skin is themeable.

const STYLE_ID = 'havik-player-styles';

const CSS = `
.havik-player{position:relative;display:block;width:100%;background:var(--havik-bg,#0e0f13);
  color:var(--havik-text,#fff);font-family:var(--havik-font,system-ui,sans-serif);
  overflow:hidden;line-height:1.4;-webkit-tap-highlight-color:transparent}
.havik-player video{display:block;width:100%;height:auto;aspect-ratio:16/9;
  object-fit:contain;background:#000;outline:none}
.havik-player:focus{outline:none}
.havik-player *{box-sizing:border-box}

.havik-player:fullscreen,.havik-player:-webkit-full-screen{display:flex;align-items:center;
  justify-content:center;width:100vw;height:100vh;aspect-ratio:auto}
.havik-player:fullscreen video,.havik-player:-webkit-full-screen video{width:100%;height:100%;
  aspect-ratio:auto;object-fit:contain}

.havik-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;cursor:pointer}
.havik-gradient{position:absolute;left:0;right:0;bottom:0;height:120px;pointer-events:none;
  background:linear-gradient(to top,rgba(0,0,0,.78),transparent);
  opacity:1;transition:opacity .25s}
.havik-player.havik-hide .havik-gradient,
.havik-player.havik-hide .havik-bar{opacity:0;pointer-events:none}
.havik-player.havik-hide{cursor:none}

.havik-center{width:74px;height:74px;border-radius:50%;border:0;cursor:pointer;
  background:var(--havik-accent,#E1B600);color:var(--havik-accent-text,#1B1C23);
  display:flex;align-items:center;justify-content:center;
  box-shadow:0 6px 24px rgba(0,0,0,.45);transition:transform .15s,opacity .15s}
.havik-center:hover{transform:scale(1.06)}
.havik-center svg{width:38px;height:38px;margin-left:2px}
.havik-center.havik-gone{opacity:0;pointer-events:none;transform:scale(.8)}

.havik-spinner{width:56px;height:56px;border-radius:50%;
  border:4px solid rgba(255,255,255,.2);border-top-color:var(--havik-accent,#E1B600);
  animation:havik-spin .8s linear infinite}
@keyframes havik-spin{to{transform:rotate(360deg)}}

/* Status card — branded full-bleed overlay for the stopped/pre-play states
   (ended / fatal error / pre-play / armed-waiting). */
.havik-status{position:absolute;inset:0;display:flex;flex-direction:column;gap:16px;
  align-items:center;justify-content:center;background:rgba(14,15,19,.82);text-align:center;padding:24px}
.havik-status__logo{width:128px;height:46px;background:var(--havik-logo) center/contain no-repeat;opacity:.95}
.havik-status__msg{color:var(--havik-text,#fff);font-size:15px;font-weight:600;max-width:480px}
.havik-status__btn{display:inline-flex;align-items:center;gap:8px;border:0;cursor:pointer;
  padding:10px 18px;border-radius:var(--havik-radius,8px);font-weight:700;font-size:14px;
  background:var(--havik-accent,#E1B600);color:var(--havik-accent-text,#1B1C23);transition:transform .15s}
.havik-status__btn:hover{transform:scale(1.04)}
.havik-status__btn svg{width:18px;height:18px}
/* Pre-play: lighter scrim so the poster shows through, and a round play CTA. */
.havik-status--preplay{background:rgba(14,15,19,.4)}
.havik-status--preplay .havik-status__btn--play{width:74px;height:74px;border-radius:50%;padding:0;
  justify-content:center;box-shadow:0 6px 24px rgba(0,0,0,.45)}
.havik-status--preplay .havik-status__btn--play svg{width:38px;height:38px;margin-left:3px}

.havik-watermark{position:absolute;top:12px;right:14px;height:22px;opacity:.85;pointer-events:none;
  content:'';background:var(--havik-logo) center/contain no-repeat;width:90px}
/* A status card carries its own logo — the corner watermark would duplicate it. */
.havik-player.havik-has-status .havik-watermark{display:none}

.havik-bar{position:absolute;left:0;right:0;bottom:0;padding:0 12px 8px;
  opacity:1;transition:opacity .25s;z-index:2}
.havik-seek{position:relative;height:16px;display:flex;align-items:center;cursor:pointer;margin-bottom:2px}
.havik-seek__track{position:relative;width:100%;height:4px;border-radius:3px;background:rgba(255,255,255,.25)}
.havik-seek__buffered{position:absolute;left:0;top:0;height:100%;border-radius:3px;background:rgba(255,255,255,.4)}
.havik-seek__played{position:absolute;left:0;top:0;height:100%;border-radius:3px;background:var(--havik-accent,#E1B600)}
.havik-seek__thumb{position:absolute;top:50%;width:13px;height:13px;border-radius:50%;
  background:var(--havik-accent,#E1B600);transform:translate(-50%,-50%) scale(0);transition:transform .1s}
.havik-seek:hover .havik-seek__thumb{transform:translate(-50%,-50%) scale(1)}

.havik-controls{display:flex;align-items:center;gap:6px;height:40px}
.havik-spacer{flex:1}
.havik-btn{display:inline-flex;align-items:center;justify-content:center;width:38px;height:38px;
  border:0;background:transparent;color:var(--havik-text,#fff);cursor:pointer;border-radius:var(--havik-radius,8px);
  padding:0;transition:background .12s,color .12s}
.havik-btn:hover{background:rgba(255,255,255,.12)}
.havik-btn:focus-visible{outline:2px solid var(--havik-accent,#E1B600);outline-offset:1px}
.havik-btn svg{width:22px;height:22px}

/* The volume level is filled in accent up to the thumb, like the seek bar's
   played span — a native range input paints no "elapsed" side, which left the
   level readable only from the dot's position. --havik-vol (0..1) is set by
   syncVolume(); the track width and thumb size live here as variables because
   the WebKit fill has to compute the thumb centre from both. */
.havik-vol{display:flex;align-items:center}
.havik-vol__slider{--havik-vol-w:70px;--havik-vol-thumb:12px;--havik-vol:1;
  --havik-vol-pos:calc(var(--havik-vol-thumb) / 2 + var(--havik-vol) * (var(--havik-vol-w) - var(--havik-vol-thumb)));
  width:0;opacity:0;transition:width .18s,opacity .18s;height:4px;cursor:pointer;
  appearance:none;-webkit-appearance:none;background:transparent;border-radius:3px;margin:0 4px}
.havik-vol:hover .havik-vol__slider,.havik-vol:focus-within .havik-vol__slider{width:var(--havik-vol-w);opacity:1}
/* WebKit/Blink have no progress pseudo-element: paint the fill as a hard-stop
   gradient that ends at the thumb centre. */
.havik-vol__slider::-webkit-slider-runnable-track{height:4px;border-radius:3px;
  background:linear-gradient(to right,var(--havik-accent,#E1B600) var(--havik-vol-pos),
  rgba(255,255,255,.3) var(--havik-vol-pos))}
/* Firefox fills natively, and tracks the thumb exactly. */
.havik-vol__slider::-moz-range-track{height:4px;border-radius:3px;background:rgba(255,255,255,.3)}
.havik-vol__slider::-moz-range-progress{height:4px;border-radius:3px;background:var(--havik-accent,#E1B600)}
.havik-vol__slider::-webkit-slider-thumb{-webkit-appearance:none;width:var(--havik-vol-thumb);
  height:var(--havik-vol-thumb);border-radius:50%;background:var(--havik-accent,#E1B600);
  margin-top:calc((4px - var(--havik-vol-thumb)) / 2)}
.havik-vol__slider::-moz-range-thumb{width:var(--havik-vol-thumb);height:var(--havik-vol-thumb);
  border:0;border-radius:50%;background:var(--havik-accent,#E1B600)}

.havik-time{font-size:12px;color:var(--havik-text-muted,#9aa0ad);
  font-variant-numeric:tabular-nums;padding:0 6px;white-space:nowrap}
.havik-live{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:700;
  letter-spacing:.4px;padding:0 8px;color:var(--havik-text,#fff)}
.havik-live svg{width:9px;height:9px;color:var(--havik-text-muted,#9aa0ad)}
.havik-live.is-live svg{color:var(--havik-accent,#E1B600)}
.havik-live__go{margin-left:2px;border:0;cursor:pointer;background:transparent;color:var(--havik-text-muted,#9aa0ad);
  font:inherit;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;padding:2px 6px;border-radius:6px}
.havik-live.is-live .havik-live__go{display:none}
.havik-live__go:hover{color:var(--havik-accent,#E1B600)}

.havik-menu{position:absolute;bottom:54px;right:12px;min-width:170px;max-height:230px;overflow:auto;
  background:var(--havik-surface,#1B1C23);border:1px solid var(--havik-border,rgba(255,255,255,.1));
  border-radius:var(--havik-radius,8px);box-shadow:0 10px 30px rgba(0,0,0,.5);padding:6px;z-index:3}
.havik-menu[hidden]{display:none}
.havik-menu__title{font-size:11px;text-transform:uppercase;letter-spacing:.6px;
  color:var(--havik-text-muted,#9aa0ad);padding:6px 8px 4px}
.havik-menu__item{display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;
  border:0;background:transparent;color:var(--havik-text,#fff);cursor:pointer;font:inherit;font-size:13px;
  text-align:left;padding:7px 8px;border-radius:6px}
.havik-menu__item:hover{background:var(--havik-surface-muted,#2a2c36)}
.havik-menu__item[aria-checked="true"]{color:var(--havik-accent,#E1B600);font-weight:600}
.havik-menu__item[aria-checked="true"]::after{content:'✓'}
`;

export function injectStyles(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  doc.head.appendChild(style);
}
